/**
 * Number formatting and calculation utilities to ensure consistent decimal handling
 * across the application, preventing rounding errors and display inconsistencies
 *
 * IMPORTANT: All number formatting in the UI should use these utilities.
 * Direct use of .toFixed() or .toLocaleString() is discouraged - ESLint will warn.
 * Maximum decimal places: 2 (enforced globally)
 */

/** Maximum decimal places allowed in UI display */
export const MAX_DECIMALS = 2;

function shiftExponent(value: number, exponentDelta: number): number {
  const [coefficient, exponent = "0"] = value.toString().split("e");
  return Number(`${coefficient}e${Number(exponent) + exponentDelta}`);
}

/**
 * Rounds a number to specified decimal places using banker's rounding (round half to even)
 * to minimize rounding bias in financial calculations
 */
export function roundToDecimals(value: number, decimals: number = MAX_DECIMALS): number {
  if (!Number.isFinite(value)) return 0;

  // Use exponential notation to avoid floating-point multiplication errors
  // e.g. 86.845 * 100 = 8684.4999... but Number("86.845e2") = 8684.5
  return shiftExponent(Math.round(shiftExponent(value, decimals)), -decimals);
}

/**
 * Formats a percentage value with consistent decimal places
 * Always shows exactly 2 decimal places for percentages
 */
export function formatPercentage(value: number, decimals: number = MAX_DECIMALS): string {
  if (!Number.isFinite(value)) return "0.00%";

  const rounded = roundToDecimals(value, decimals);
  // eslint-disable-next-line no-restricted-syntax
  return `${rounded.toFixed(decimals)}%`;
}

/**
 * Formats a signed percentage with explicit + / - prefix while preserving decimal consistency.
 */
export function formatSignedPercentage(value: number, decimals: number = MAX_DECIMALS): string {
  if (!Number.isFinite(value)) return "0.00%";

  const sign = value >= 0 ? "+" : "-";
  return `${sign}${formatPercentage(Math.abs(value), decimals)}`;
}

/**
 * Formats a currency value with consistent decimal places
 * Always shows exactly 2 decimal places for currency
 */
export function formatCurrency(value: number, decimals: number = MAX_DECIMALS): string {
  if (!Number.isFinite(value)) return "$0.00";

  const rounded = roundToDecimals(value, decimals);
  // eslint-disable-next-line no-restricted-syntax
  return `$${rounded.toFixed(decimals)}`;
}

/**
 * Formats a currency value with thousands separators and fixed decimals
 */
export function formatCurrencyWithCommas(value: number, decimals: number = MAX_DECIMALS): string {
  if (!Number.isFinite(value)) return "$0.00";

  const rounded = roundToDecimals(value, decimals);
  // eslint-disable-next-line no-restricted-syntax
  return `$${rounded.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

/**
 * Formats a currency delta with thousands separators and an explicit sign.
 * Positive values represent inflows and negative values represent outflows.
 */
export function formatSignedCurrencyWithCommas(value: number, decimals: number = MAX_DECIMALS): string {
  if (!Number.isFinite(value)) return "$0.00";
  if (value === 0) return formatCurrencyWithCommas(0, decimals);

  const sign = value > 0 ? "+" : "-";
  return `${sign}${formatCurrencyWithCommas(Math.abs(value), decimals)}`;
}

/**
 * Formats a currency value with an ISO currency code prefix and thousands separators.
 * Used when the currency may differ per row (e.g. trades on foreign exchanges).
 *
 * - When currencyCode is provided, prefixes with that code: "JPY 56,010.00"
 * - When currencyCode is undefined (contract lookup failed/pending), shows raw number only: "56,010.00"
 */
export function formatCurrencyWithSymbol(
  value: number,
  currencyCode?: string,
  decimals: number = MAX_DECIMALS,
): string {
  if (!Number.isFinite(value)) {
    const zero = (0).toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return currencyCode ? `${currencyCode} ${zero}` : zero;
  }

  const rounded = roundToDecimals(value, decimals);
  // eslint-disable-next-line no-restricted-syntax
  const formatted = rounded.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return currencyCode ? `${currencyCode} ${formatted}` : formatted;
}

/**
 * Formats a currency value as a rounded thousands shorthand (e.g., $147K)
 */
export function formatCurrencyK(value: number): string {
  if (!Number.isFinite(value)) return "$0K";

  const thousands = roundToDecimals(value / 1000, 0);
  return `$${formatNumber(thousands, 0)}K`;
}

/**
 * Abbreviates a large number into a compact K/M/B/T form for table display.
 * Decimals follow the magnitude of the abbreviated mantissa: 2 when it is < 10
 * (e.g. "1.24M"), otherwise 1 (e.g. "20.4B", "200.3M"). Values below 1,000 are
 * shown in full with no suffix. Returns the mantissa + suffix only (no symbol).
 */
function abbreviateLargeNumber(value: number): string {
  if (!Number.isFinite(value)) return "0";

  const abs = Math.abs(value);
  const tiers: [number, string][] = [
    [1e12, "T"],
    [1e9, "B"],
    [1e6, "M"],
    [1e3, "K"],
  ];

  for (let i = 0; i < tiers.length; i++) {
    const [threshold, suffix] = tiers[i];
    if (abs >= threshold) {
      let unit = threshold;
      let unitSuffix = suffix;
      let decimals = Math.abs(value / unit) < 10 ? 2 : 1;
      let rounded = roundToDecimals(value / unit, decimals);
      // Rounding can push a value just under the next tier (e.g. 999,950) to
      // "1000.0K"; promote it to the next unit up ("1.00M") instead.
      if (Math.abs(rounded) >= 1000 && i > 0) {
        unit = tiers[i - 1][0];
        unitSuffix = tiers[i - 1][1];
        decimals = Math.abs(value / unit) < 10 ? 2 : 1;
        rounded = roundToDecimals(value / unit, decimals);
      }
      // eslint-disable-next-line no-restricted-syntax
      return `${rounded.toFixed(decimals)}${unitSuffix}`;
    }
  }

  return formatNumber(value, 0);
}

/**
 * Formats a market-cap value for compact table display (no currency symbol;
 * the currency is shown in its own adjacent column).
 * - >= 1M: abbreviated with an M/B/T suffix (e.g. "1.24M", "20.4B").
 * - < 1M: full number with thousands separators (e.g. "40,000.00").
 * Accepts a number or numeric string (the contracts API returns Decimal as string).
 */
export function formatMarketCap(value: number | string): string {
  const num = safeParseFloat(value);

  if (Math.abs(num) < 1e6) {
    return formatCurrencyWithSymbol(num, undefined, 2);
  }

  return abbreviateLargeNumber(num);
}

/**
 * Compact currency for tight visual labels (e.g. chart/waterfall values that
 * must fit inside a small bar): "$30.7M", "$14.2M", "$327". Keeps a leading
 * symbol at every magnitude. For precise/exact display use formatCurrencyWithCommas.
 * Accepts a number or numeric string (Decimal as string).
 */
export function formatCompactCurrency(value: number | string): string {
  const num = safeParseFloat(value);
  if (Math.abs(num) >= 1e3) return `$${abbreviateLargeNumber(num)}`;
  return formatCurrencyWithCommas(num, 0);
}

/**
 * Money expressed in thousands, with no higher tier, so two figures stay
 * directly comparable at a glance: 1_000 -> "$1K", 1_000_000 -> "$1,000K".
 * Amounts under $1K keep their exact dollar value ("$850") rather than
 * collapsing to "$1K".
 */
export function formatCurrencyThousands(value: number): string {
  if (!Number.isFinite(value)) return "$0";
  if (Math.abs(value) < 1000) return formatCurrencyWithCommas(value, 0);
  return formatCurrencyK(value);
}

/**
 * Formats a trading-volume value for compact table display.
 * Abbreviated with a K/M/B/T suffix (e.g. "1.63M", "200.3M"); values below 1,000
 * are shown in full. Accepts a number or numeric string (Decimal as string).
 */
export function formatVolume(value: number | string): string {
  return abbreviateLargeNumber(safeParseFloat(value));
}

/**
 * Formats a price value with consistent decimal places
 * Always shows exactly 2 decimal places for prices
 */
export function formatPrice(value: number, currency: string = "USD", decimals: number = MAX_DECIMALS): string {
  if (!Number.isFinite(value)) return `${currency} 0.00`;

  const rounded = roundToDecimals(value, decimals);
  // eslint-disable-next-line no-restricted-syntax
  return `${currency} ${rounded.toFixed(decimals)}`;
}

/**
 * Safely parses a string or number to a float, returning 0 for invalid values
 */
export function safeParseFloat(value: string | number | undefined | null): number {
  if (value === null || value === undefined) return 0;

  const parsed = typeof value === "string" ? parseFloat(value) : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Calculates a sum of values with proper decimal handling
 * Rounds the final result to prevent floating point precision issues
 */
export function safeSum(values: (number | string | undefined | null)[], decimals: number = MAX_DECIMALS): number {
  const sum = values.reduce((acc: number, val) => {
    return acc + safeParseFloat(val);
  }, 0);

  return roundToDecimals(sum, decimals);
}

/**
 * Checks if a total equals 100% within a tolerance (default 0.01%)
 * Used for basket and portfolio weight validation
 */
export function isExactly100Percent(total: number, tolerance: number = 0.01): boolean {
  return Math.abs(total - 100) < tolerance;
}

/**
 * Calculates the difference from 100% for allocation messages
 */
export function getAllocationDifference(total: number): {
  isOver: boolean;
  difference: number;
  message: string;
} {
  const rounded = roundToDecimals(total, 2);
  const difference = roundToDecimals(Math.abs(100 - rounded), 2);
  const isOver = rounded > 100;

  let message = "";
  if (difference > 0) {
    message = isOver
      ? `Over-allocated by ${formatPercentage(difference)}`
      : `Allocation remaining: ${formatPercentage(difference)}`;
  }

  return { isOver, difference, message };
}

/**
 * Distributes a total amount proportionally among weights
 * Ensures the sum equals exactly the target total
 */
export function distributeProportionally(weights: number[], targetTotal: number, decimals: number = MAX_DECIMALS): number[] {
  if (weights.length === 0) return [];

  const totalWeight = safeSum(
    weights.map(w => w),
    decimals
  );
  if (totalWeight === 0) return weights.map(() => 0);

  // Calculate proportional amounts
  const proportional = weights.map(weight => roundToDecimals((weight / totalWeight) * targetTotal, decimals));

  // Handle rounding differences by adjusting the largest value
  const currentSum = safeSum(
    proportional.map(p => p),
    decimals
  );
  const difference = roundToDecimals(targetTotal - currentSum, decimals);

  if (difference !== 0) {
    // Find the index of the largest value to adjust
    const maxIndex = proportional.reduce((maxIdx, val, idx) => (val > proportional[maxIdx] ? idx : maxIdx), 0);
    proportional[maxIndex] = roundToDecimals(proportional[maxIndex] + difference, decimals);
  }

  return proportional;
}

/**
 * Scales values to sum to a target total while maintaining proportions
 */
export function scaleToTotal(values: number[], targetTotal: number, decimals: number = MAX_DECIMALS): number[] {
  const currentTotal = safeSum(
    values.map(v => v),
    decimals
  );
  if (currentTotal === 0) return values;

  const scaleFactor = targetTotal / currentTotal;
  return distributeProportionally(
    values.map(val => val * scaleFactor),
    targetTotal,
    decimals
  );
}

/**
 * Validates that basket/portfolio weights are within acceptable ranges
 * @param weight - The weight value to validate
 * @param context - The context for validation ('individual' allows >100%, 'total' enforces <=100%)
 */
export function validateWeight(
  weight: number,
  context: "individual" | "total" = "total"
): {
  isValid: boolean;
  error?: string;
} {
  if (!Number.isFinite(weight)) {
    return { isValid: false, error: "Weight must be a valid number" };
  }

  // Allow individual stocks to exceed 100% but enforce limits for total/basket weights
  if (context === "total" && Math.abs(weight) > 100) {
    return { isValid: false, error: "Weight cannot exceed 100%" };
  }

  return { isValid: true };
}

/**
 * Ensures consistent decimal formatting for display
 */
export function formatWeight(weight: number, decimals: number = MAX_DECIMALS): string {
  if (!Number.isFinite(weight)) return "0.00";

  const rounded = roundToDecimals(weight, decimals);
  // eslint-disable-next-line no-restricted-syntax
  return rounded.toFixed(decimals);
}

/**
 * General-purpose number formatter with max 2 decimal places
 * Removes trailing zeros for cleaner display (e.g., 1.50 -> "1.5", 2.00 -> "2")
 */
export function formatNumber(value: number, maxDecimals: number = MAX_DECIMALS): string {
  if (!Number.isFinite(value)) return "0";

  const rounded = roundToDecimals(value, maxDecimals);
  // eslint-disable-next-line no-restricted-syntax
  return rounded.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  });
}

/** Preserves the stored finite number without display rounding or locale separators. */
export function formatUnroundedNumber(numericValue: number): string {
  if (!Number.isFinite(numericValue)) {
    return "";
  }
  return String(numericValue);
}

/**
 * Formats a small count for compact display (e.g. like counts on cards).
 * Values below 1,000 are shown in full; larger values use a single-decimal
 * lowercase suffix (k/m/b) with trailing ".0" trimmed -- e.g. 42 -> "42",
 * 987 -> "987", 1630 -> "1.6k", 12000 -> "12k", 3300000 -> "3.3m".
 */
export function formatCompactCount(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "0";

  const tiers: [number, string][] = [
    [1e9, "b"],
    [1e6, "m"],
    [1e3, "k"],
  ];

  for (let i = 0; i < tiers.length; i++) {
    const [threshold] = tiers[i];
    if (value >= threshold) {
      let mantissa = roundToDecimals(value / threshold, 1);
      let suffix = tiers[i][1];
      // Rounding can push a value just under the next tier (e.g. 999,950) to
      // "1000k"; promote it to the next unit up ("1m") instead.
      if (mantissa >= 1000 && i > 0) {
        mantissa = roundToDecimals(value / tiers[i - 1][0], 1);
        suffix = tiers[i - 1][1];
      }
      // eslint-disable-next-line no-restricted-syntax
      const text = mantissa.toFixed(1).replace(/\.0$/, "");
      return `${text}${suffix}`;
    }
  }

  return String(Math.floor(value));
}

/**
 * Formats a number with exactly the specified decimal places (no trailing zero removal)
 * Use when you need consistent column alignment in tables
 */
export function formatNumberFixed(value: number, decimals: number = MAX_DECIMALS): string {
  if (!Number.isFinite(value)) return "0".padEnd(decimals + 2, "0").replace(/^0/, "0.");

  const rounded = roundToDecimals(value, decimals);
  // eslint-disable-next-line no-restricted-syntax
  return rounded.toFixed(decimals);
}

/**
 * Calculates equal weights for a given number of items
 * Ensures the sum equals exactly 100%
 */
export function calculateEqualWeights(count: number, decimals: number = MAX_DECIMALS): number[] {
  if (count <= 0) return [];

  const baseWeight = roundToDecimals(100 / count, decimals);
  const weights = Array(count).fill(baseWeight);

  // Handle remainder to ensure exact 100%
  const currentSum = safeSum(weights, decimals);
  const remainder = roundToDecimals(100 - currentSum, decimals);

  if (remainder !== 0) {
    weights[0] = roundToDecimals(weights[0] + remainder, decimals);
  }

  return weights;
}

/**
 * Formats a file size in bytes to a human-readable string
 * Uses appropriate units (B, KB, MB, GB) with max 1 decimal place
 */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes === 0) return "0 B";

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = bytes / Math.pow(k, i);

  // Use 1 decimal for values < 10, otherwise round to integer
  const formatted = size < 10 ? roundToDecimals(size, 1) : Math.round(size);
  return `${formatted} ${sizes[Math.min(i, sizes.length - 1)]}`;
}
